import React, { useEffect, useState } from "react";
import { trackService } from "../utils/track";
import { useNavigate } from "react-router-dom";
import emailjs from "emailjs-com";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/autoplay";

import NetworkFabric from "./NetworkFabric";
import StepArt from "./StepArt";
import GlobeOffices from "./GlobeOffices";
import Modal from "./Modal";
import useReveal from "../hooks/useReveal";
import { scrollToSection } from "../utils/scroll";
import {
  CONTACT,
  coreValues,
  engagementSteps,
  leaders,
  partnerLogos,
  practices,
} from "../data/content";

// Delivery identifiers come from the environment — see the README. They are
// public, browser-visible values, but keeping them out of the repository means
// each environment sends through its own EmailJS service.
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || "";
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "";
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "";
const EMAILJS_READY = Boolean(EMAILJS_SERVICE_ID && EMAILJS_TEMPLATE_ID && EMAILJS_PUBLIC_KEY);

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  location: "",
  subject: "",
  message: "",
};

const PRACTICE_ICONS = {
  ITConsulting: (
    <>
      <rect x="2" y="3.5" width="16" height="10.5" rx="1.6" fill="none" stroke="#0640DC" strokeWidth="1.6" />
      <path d="M7 17h6" stroke="#0640DC" strokeWidth="1.6" />
    </>
  ),
  ConnectivitySolutions: (
    <>
      <circle cx="10" cy="10" r="2" fill="#0640DC" />
      <path
        d="M5.5 14.5a6.4 6.4 0 0 1 0-9M14.5 5.5a6.4 6.4 0 0 1 0 9M3 17a10 10 0 0 1 0-14M17 3a10 10 0 0 1 0 14"
        fill="none"
        stroke="#0640DC"
        strokeWidth="1.5"
      />
    </>
  ),
  CommunicationSolutions: (
    <path
      d="M6.5 3.5 8.8 7 7 9c.9 1.9 2.3 3.3 4 4l2-1.8 3.5 2.3-1.6 2.6c-4.7.9-11-5.4-10.1-10.1z"
      fill="none"
      stroke="#0640DC"
      strokeWidth="1.6"
    />
  ),
  SecuritySolutions: (
    <>
      <path d="M10 2.4 16.6 5v4.6c0 3.9-2.8 6.6-6.6 7.6-3.8-1-6.6-3.7-6.6-7.6V5z" fill="none" stroke="#0640DC" strokeWidth="1.6" />
      <path d="m7.4 10 1.9 2 3.4-4" fill="none" stroke="#0640DC" strokeWidth="1.6" />
    </>
  ),
  DataStorage: (
    <>
      <ellipse cx="10" cy="5" rx="6.4" ry="2.6" fill="none" stroke="#0640DC" strokeWidth="1.6" />
      <path
        d="M3.6 5v10c0 1.4 2.9 2.6 6.4 2.6s6.4-1.2 6.4-2.6V5M3.6 10c0 1.4 2.9 2.6 6.4 2.6s6.4-1.2 6.4-2.6"
        fill="none"
        stroke="#0640DC"
        strokeWidth="1.6"
      />
    </>
  ),
};

function Home() {
  const navigate = useNavigate();

  /* ---------------- contact form (EmailJS) ---------------- */
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [sending, setSending] = useState(false);
  const [popup, setPopup] = useState({ isVisible: false, message: "", ok: true });

  useEffect(() => {
    if (EMAILJS_READY) emailjs.init(EMAILJS_PUBLIC_KEY);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!EMAILJS_READY) {
      setPopup({
        isVisible: true,
        ok: false,
        message:
          "The contact form is not configured for this environment. Please email us directly at " +
          `${CONTACT.email}.`,
      });
      return;
    }

    setSending(true);

    const templateParams = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      location: formData.location,
      subject: formData.subject,
      message: formData.message,
      from_name: formData.name,
      from_email: formData.email,
      reply_to: formData.email,
    };

    emailjs
      .send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY)
      .then(
        () => {
          setPopup({
            isVisible: true,
            ok: true,
            message: "Your message has been sent successfully!",
          });
          setFormData(EMPTY_FORM);
        },
        (err) => {
          // eslint-disable-next-line no-console
          console.error("EmailJS error:", err);
          setPopup({
            isVisible: true,
            ok: false,
            message: "Failed to send message. Please try again.",
          });
        }
      )
      .finally(() => setSending(false));
  };

  useReveal([]);

  const goToService = (id, title) => {
    const path = `/Services/Services#${id}`;
    // Recorded before navigating; the request uses keepalive so it survives
    // the page change.
    trackService(title, path);
    navigate(path);
  };

  return (
    <>
      {/* ══════════ hero ══════════ */}
      <section className="hero" id="home">
        <NetworkFabric />
        <div className="wrap">
          <div className="hero-inner solo">
            <div>
              <span className="eyebrow mono">IT Consulting · Connectivity · Security</span>
              <h1>
                Transforming Challenges Into <em>Tech Solutions</em>
              </h1>
              <p className="lede">
                New Age Services is a comprehensive IT consulting partner dedicated to empowering
                organizations with tailored technology solutions — delivered across the United
                States, Canada and India, and run by our own operations team.
              </p>
              <div className="cta">
                <button type="button" className="btn" onClick={() => scrollToSection("about")}>
                  Get Started
                  <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                    <path d="M2 8h11M9 4l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.8" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ about ══════════ */}
      <section className="band light" id="about">
        <div className="wrap">
          <div className="head reveal">
            <span className="mono key">About Us</span>
            <h2>A comprehensive IT consulting partner, not another vendor on the list.</h2>
            <p className="sub">
              In today's fast-paced digital landscape, businesses face numerous challenges — from
              managing complex IT infrastructures to ensuring data security and optimizing
              operational efficiency. New Age Services stands as a comprehensive IT consulting
              partner dedicated to empowering organizations with tailored technology solutions.
            </p>
          </div>

          <div className="split reveal" style={{ marginBottom: 76 }}>
            <div>
              <span className="mono key">Our Vision and Mission</span>
              <h3 style={{ fontSize: 26, margin: "12px 0 16px" }}>
                Bridging the gap between technology and business needs.
              </h3>
              <p className="sub" style={{ marginBottom: 14 }}>
                Founded with the vision to bridge the gap between technology and business needs, New
                Age Services has evolved into a trusted partner for organizations seeking reliable
                IT solutions. Our commitment to excellence and innovation drives us to continuously
                adapt to the ever-changing technology landscape.
              </p>
              <p className="sub">
                Our mission is to empower businesses through innovative technology solutions that
                enhance productivity and ensure seamless operations. We envision a future where
                every organization can leverage technology to achieve its full potential, fostering
                growth and resilience.
              </p>
            </div>
            <img className="shot wide" src="/background-about.jpg" alt="New Age Services" loading="lazy" />
          </div>

          <div className="head reveal" style={{ maxWidth: "58ch" }}>
            <span className="mono key">How we engage</span>
            <h2>Four steps, in this order, on every engagement.</h2>
          </div>
          <div className="steps reveal" style={{ marginBottom: 76 }}>
            {engagementSteps.map((s, i) => (
              <div className="step" key={s.title}>
                <StepArt name={s.title} />
                <div className="t">
                  <span className="vnum">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h4>{s.title}</h4>
                    <p>{s.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="head reveal" style={{ maxWidth: "56ch" }}>
            <span className="mono key">Core Values</span>
            <h2>Eight commitments that decide how we work.</h2>
          </div>
          <div className="split reveal" style={{ alignItems: "start" }}>
            <ul className="vals">
              {coreValues.map((v, i) => (
                <li key={v.title}>
                  <span className="vnum">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <b>{v.title}</b>
                    <p>{v.body}</p>
                  </div>
                </li>
              ))}
            </ul>
            <img
              className="shot values-art"
              src="/core-values-icon.jpg"
              alt="Core values"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* ══════════ partners ══════════ */}
      <section className="band white" id="clients">
        <div className="wrap">
          <div className="head reveal" style={{ marginBottom: 26 }}>
            <span className="mono key">Our Partners</span>
            <h2>One contract, backed by the vendors you already trust.</h2>
          </div>
          <div className="partners">
            <Swiper
              modules={[Autoplay]}
              spaceBetween={40}
              slidesPerView={2}
              breakpoints={{
                640: { slidesPerView: 3 },
                900: { slidesPerView: 4 },
                1200: { slidesPerView: 5 },
              }}
              autoplay={{ delay: 1500, disableOnInteraction: false }}
              loop
            >
              {partnerLogos.map((src, i) => (
                <SwiperSlide key={src}>
                  <img src={src} alt={`Partner ${i + 1}`} loading="lazy" />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </section>

      {/* ══════════ services ══════════ */}
      <section className="band light" id="services">
        <div className="wrap">
          <div className="head reveal">
            <span className="mono key">Our Services</span>
            <h2>Five practices covering the full estate.</h2>
            <p className="sub">
              New Age Services offers a comprehensive suite of IT solutions designed to meet the
              diverse needs of modern enterprises.
            </p>
          </div>
          <div className="caps reveal">
            {practices.map((p) => (
              <div className="cap" key={p.id}>
                <div className="ico">
                  <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
                    {PRACTICE_ICONS[p.id]}
                  </svg>
                </div>
                <h3>{p.title}</h3>
                <p>{p.body}</p>
                <button type="button" className="rm" onClick={() => goToService(p.id, p.title)}>
                  Read more →
                </button>
              </div>
            ))}
            <div className="cap invite">
              <h3>Not sure where to start?</h3>
              <p>
                A short discovery call maps your estate across all five practices and shows where
                cost, risk and downtime are hiding.
              </p>
              <button
                type="button"
                className="btn white"
                style={{ alignSelf: "flex-start", marginTop: 6 }}
                onClick={() => scrollToSection("contact")}
              >
                Contact us to know more
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ leaders ══════════ */}
      <section className="band" id="leaders">
        <div className="wrap">
          <div className="head reveal">
            <span className="mono key">Our Leaders</span>
            <h2>
              Meet the visionary individuals driving our company toward success and innovation.
            </h2>
            <p className="sub">
              Their leadership inspires excellence and sets new standards in the industry.
            </p>
          </div>
          <div className="two reveal">
            {leaders.map((l) => (
              <div className="person" key={l.name}>
                <img src={l.image} alt={l.name} loading="lazy" />
                <div>
                  <h3 style={{ fontSize: 18 }}>{l.name}</h3>
                  <p className="mono role">{l.role}</p>
                  <p>{l.body}</p>
                  {l.linkedin && (
                    <a
                      className="person-link"
                      href={l.linkedin}
                      target="_blank"
                      /* noopener stops the opened page reaching back through
                         window.opener; noreferrer keeps our URL out of its logs. */
                      rel="noopener noreferrer"
                      aria-label={`${l.name} on LinkedIn (opens in a new tab)`}
                    >
                      <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
                        <path
                          d="M3.9 5.5H2.1V14h1.8zM3 2.2a1.1 1.1 0 1 0 0 2.2 1.1 1.1 0 0 0 0-2.2M14 9.3c0-2.2-1.2-3.2-2.7-3.2-1.2 0-1.8.7-2.1 1.2V5.5H7.4V14h1.8V9.4c0-1 .5-1.7 1.4-1.7s1.5.6 1.5 1.7V14H14z"
                          fill="currentColor"
                        />
                      </svg>
                      LinkedIn
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ contact ══════════ */}
      <section className="band mid" id="contact">
        <div className="wrap">
          <div className="head reveal">
            <span className="mono key">Contact</span>
            <h2>Get in touch with us for any inquiries, and we'll be happy to assist you.</h2>
          </div>

          <div className="contact-grid reveal">
            <div className="contact-aside">
              <div className="info info-globe">
                <span className="ic">
                  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                    <circle cx="8" cy="8" r="6.2" fill="none" stroke="#9DB6F7" strokeWidth="1.5" />
                    <path d="M1.8 8h12.4M8 1.8a12 12 0 0 1 0 12.4M8 1.8a12 12 0 0 0 0 12.4" fill="none" stroke="#9DB6F7" strokeWidth="1.3" />
                  </svg>
                </span>
                <h4>Our Offices</h4>
                <p className="sub" style={{ fontSize: 15 }}>
                  Delivering across three regions
                </p>
                <GlobeOffices />
              </div>

              <div className="info">
                <span className="ic">
                  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                    <rect x="1.8" y="3.4" width="12.4" height="9.2" rx="1.6" fill="none" stroke="#9DB6F7" strokeWidth="1.5" />
                    <path d="m2.4 4.4 5.6 4.2 5.6-4.2" fill="none" stroke="#9DB6F7" strokeWidth="1.5" />
                  </svg>
                </span>
                <h4>Email Us</h4>
                <p className="sub" style={{ fontSize: 15 }}>
                  <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>
                </p>
              </div>

              <div className="info">
                <span className="ic">
                  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                    <circle cx="8" cy="8" r="6.2" fill="none" stroke="#9DB6F7" strokeWidth="1.5" />
                    <path d="M8 4.4V8l2.6 1.6" fill="none" stroke="#9DB6F7" strokeWidth="1.5" />
                  </svg>
                </span>
                <h4>Open Hours</h4>
                <p className="sub" style={{ fontSize: 15 }}>{CONTACT.hours}</p>
              </div>
            </div>

            <form className="panel panel-form" onSubmit={handleSubmit}>
              <div className="formgrid">
                <input
                  className="inp"
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  aria-label="Your Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
                <input
                  className="inp"
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  aria-label="Your Email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
                <input
                  className="inp"
                  type="tel"
                  name="phone"
                  placeholder="Mobile No."
                  aria-label="Mobile No."
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
                <input
                  className="inp"
                  type="text"
                  name="location"
                  placeholder="Your location"
                  aria-label="Your location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                />
                <input
                  className="inp span2"
                  type="text"
                  name="subject"
                  placeholder="Subject"
                  aria-label="Subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                />
                <textarea
                  className="inp span2"
                  name="message"
                  rows="4"
                  placeholder="Message"
                  aria-label="Message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                />
                <div className="form-foot">
                  <button type="submit" className="btn" disabled={sending}>
                    {sending ? (
                      <span className="load" aria-label="Sending">
                        <i />
                        <i />
                        <i />
                      </span>
                    ) : (
                      "Send Message"
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>

      <Modal
        isOpen={popup.isVisible}
        onClose={() => setPopup({ isVisible: false, message: "", ok: true })}
        title={popup.ok ? "Message sent" : "Message not sent"}
      >
        <p style={{ color: "var(--fg-ink-dim)" }}>{popup.message}</p>
        <button
          type="button"
          className="btn"
          style={{ alignSelf: "flex-start" }}
          onClick={() => setPopup({ isVisible: false, message: "", ok: true })}
        >
          Close
        </button>
      </Modal>
    </>
  );
}

export default Home;
